import { BREAKPOINTS } from "../Constants/breakpoints";
import { PersonalisationResponse } from "../Aprimo/aprimo-tracking";
import { getIsAprimoEnabled } from "../Utils/config-utils";

class MainHero {
    private element: HTMLElement;
    private sibling: HTMLElement | null;
    private initialSiblingPadding: number = 0;

    constructor(element: HTMLElement) {
        this.element = element;
        this.sibling = element.nextSibling as HTMLElement;
        this.init();
    }

    private init(): void {
        const desktopImages = this.element.querySelectorAll<HTMLImageElement>(".main-hero__desktop-image");
        const mobileImages = this.element.querySelectorAll<HTMLImageElement>(".main-hero__mobile-image");

        const heroImageSlotId = this.element.closest("[aprimo-slot-id]")?.getAttribute("aprimo-slot-id");

        // Check each immage has an aprimo-asset-id for the Aprimo call
        const allDesktopHaveId = Array.from(desktopImages).every(img =>
            img.getAttribute("aprimo-asset-id") !== null && img.getAttribute("aprimo-asset-id") !== ""
        );

        const allMobileHaveId = Array.from(mobileImages).every(img =>
            img.getAttribute("aprimo-asset-id") !== null && img.getAttribute("aprimo-asset-id") !== ""
        );

        // Aprimo SDK is only loaded when the integration is enabled ("Aprimo:Enabled" app setting)
        const aprimoEnabled = getIsAprimoEnabled()
            && typeof (globalThis as any).Aprimo?.personalisation?.sendPersonalisationRequest === "function";

        if (!aprimoEnabled) {
            this.randomlyAssignHeroImage(desktopImages, mobileImages);
        } else if (!!heroImageSlotId && allDesktopHaveId && allMobileHaveId) {
            // It's possible the sendPersonalisationRequest call fails in such a way that never hits callback at all.
            // To handle this, randomly select one of the images after timeout ms.
            const timeout = 3000;

            let fallbackTimer: ReturnType<typeof setTimeout>;

            fallbackTimer = setTimeout(() => {
                this.randomlyAssignHeroImage(desktopImages, mobileImages);

                window.appInsights?.trackException({ exception: new Error('Aprimo sendPersonalisationRequest failed to hit callback') });
            }, timeout);

            (globalThis as any).Aprimo.personalisation.sendPersonalisationRequest({
                personalisationType: "recommendation",
                details: {
                    type: "asset",
                    quantity: 1,
                    context: heroImageSlotId,
                },
                callback: (error: any, response: PersonalisationResponse[]) => {
                    // Call succeeded - remove fallback
                    clearTimeout(fallbackTimer)

                    if (error) {
                        console.error(error);
                        this.randomlyAssignHeroImage(desktopImages, mobileImages);

                        window.appInsights?.trackException({ exception: new Error('Aprimo sendPersonalisationRequest failed with error: ' + error) });
                    } else {
                        if (response != null && response.length > 0) {
                            const assetIdToMatch = response[0].code;

                            // Check we only have one asset that matches the provided asset ID,
                            // Not 0 or > 1
                            const matchingDesktop = Array.from(desktopImages).filter(
                                img => img.getAttribute("aprimo-asset-id") === assetIdToMatch
                            );

                            const matchingMobile = Array.from(mobileImages).filter(
                                img => img.getAttribute("aprimo-asset-id") === assetIdToMatch
                            );

                            if (matchingDesktop.length === 1 && matchingMobile.length === 1) {
                                desktopImages.forEach((img) => {
                                    if (img.getAttribute("aprimo-asset-id") != assetIdToMatch) {
                                        img.remove();
                                    }
                                    else {
                                        img.style.display = "block";
                                    }
                                })

                                mobileImages.forEach((img) => {
                                    if (img.getAttribute("aprimo-asset-id") != assetIdToMatch) {
                                        img.remove();
                                    }
                                    else {
                                        img.style.display = "block";
                                    }
                                })
                            } else {
                                this.randomlyAssignHeroImage(desktopImages, mobileImages);

                                window.appInsights?.trackException({ exception: new Error('Aprimo sendPersonalisationRequest asset ID did not match one available assets') });
                            }
                        } else {
                            this.randomlyAssignHeroImage(desktopImages, mobileImages);

                            window.appInsights?.trackException({ exception: new Error('Aprimo sendPersonalisationRequest returned invalid response') });
                        }
                    }
                }
            });

        } else { // Do not have the data we need for the Aprimo call
            this.randomlyAssignHeroImage(desktopImages, mobileImages);

            window.appInsights?.trackException({ exception: new Error('aprimo-slot-id or aprimo-asset-id data missing from main hero for Aprimo recommendation. Check data is set in CMS') });
        }

        if (this.sibling) {
            const computedStyle = window.getComputedStyle(this.sibling);
            this.initialSiblingPadding = parseInt(computedStyle.paddingTop) || 0;
        }

        this.updateSiblingPadding();
        window.addEventListener("resize", () => this.updateSiblingPadding());
    };

    private randomlyAssignHeroImage(desktopImages: NodeListOf<HTMLImageElement>, mobileImages: NodeListOf<HTMLImageElement>): void {

        const randomIndex = Math.floor(Math.random() * desktopImages.length);

        desktopImages.forEach((img, index) => {
            if (index != randomIndex) {
                img.remove();
            }
            else {
                img.style.display = "block";
            }
        })

        mobileImages.forEach((img, index) => {
            if (index != randomIndex) {
                img.remove();
            }
            else {
                img.style.display = "block";
            }
        })
    }

    private updateSiblingPadding(): void {
        if (!this.sibling) return;

        let additionalPadding = 0;

        if (window.innerWidth < BREAKPOINTS.mobile) {
            additionalPadding = 125;
        } else if (window.innerWidth < BREAKPOINTS.tablet) {
            additionalPadding = 175;
        } else if (window.innerWidth < BREAKPOINTS.desktop) {
            additionalPadding = 225;
        }

        const totalPadding = this.initialSiblingPadding + additionalPadding;

        this.sibling.style.setProperty('padding-top', `${totalPadding}px`, 'important');
    }
};

document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll('.main-hero').forEach(element => {
        new MainHero(element as HTMLElement);
    });
});
